import { parseChatChart } from './chart';
import { parseChatTable } from './table';
import type {
  BekkiChatApi,
  BekkiServerErrorCode,
  ChatChart,
  ChatContext,
  ChatEvent,
  ChatRequest,
  ChatTable
} from './types';

interface MockDepot {
  id: number;
  externalDepotId: string;
  locationName: string;
  tenant: { id: string };
}

interface MockTurn {
  isAborted: boolean;
  timeoutId: ReturnType<typeof setTimeout> | null;
  cancelWait: (() => void) | null;
}

const STEP_READING_MS = 700;
const STEP_ANALYZING_MS = 600;

export const DEFAULT_ANSWER_CHUNKS = [
  '**17 tours** are scheduled in depot **0052** today, ',
  'and **14** of them are already on the road.\n\n',
  '### Where it stands\n\n',
  '- **3 tours** have not started yet\n',
  '- **2 tours** are running more than **30 minutes** behind plan\n',
  '- **12 tours** are on time or ahead of plan\n\n',
  'Both delayed tours left the depot late — the first scan ',
  'was at **09:41**, roughly **50 minutes** after the planned ',
  'departure, and neither has caught up since.\n\n',
  'Across the depot **1,284 parcels** are out for delivery and ',
  '**63** are already marked as not delivered, which is about ',
  '**4.9%** of the volume handled so far today.'
];

export const WEATHER_ANSWER_CHUNKS = [
  'I can only help with Back Office data — tours, parcels, ',
  'depots and subcontractors. ',
  "I don't have weather information."
];

export const XSS_ANSWER_CHUNKS = [
  'Here is the raw tool output: ',
  '<script>alert(1)</script><b>bold</b>'
];

const MOCK_TOUR_ID = '3f6c2a1e-8b7d-4c1a-9e2f-5a4b3c2d1e0f';

const getIsoDate = (daysAgo: number): string =>
  new Date(Date.now() - daysAgo * 86_400_000).toISOString().slice(0, 10);

const getDepotName = (depot: MockDepot | undefined): string =>
  `${depot?.externalDepotId} ${depot?.locationName}`;

const getLinkAnswerChunks = (
  { dcId, tenantId, page }: ChatContext,
  depots: MockDepot[]
): string[] => {
  const home = depots.find((depot) => depot.id === dcId) ?? depots[0];
  const other = depots.find((depot) => depot.id !== home?.id);
  const tourId = page?.entities?.tourId ?? MOCK_TOUR_ID;
  const depot = `?depotId=${home?.id}`;
  const tenant = `?tenantId=${tenantId ?? home?.tenant.id}`;
  const today = getIsoDate(0);
  const weekAgo = getIsoDate(7);
  const yesterday = getIsoDate(1);

  return [
    `**2 tours** in depot **${getDepotName(home)}** are more than **30 minutes** behind plan. `,
    '[Tour 48',
    `35](bo:tour/${tourId}${depot}) left **52 min** late and `,
    `[Tour 4818](bo:tour/7a1b2c3d-4e5f-4a6b-8c7d-9e0f1a2b3c4d${depot}) **47 min** late.\n\n`,
    '### Worth a look\n\n',
    `- [Tour 4835 over the last week](bo:tour-performance/4835/${weekAgo}/${today}${depot}) `,
    `started late on **4 of 7** days, see its [driven route](bo:tour-history/${tourId}${depot})\n`,
    `- [Yesterday's activities](bo:activities/${yesterday}${depot}) show **3 tours** without a first scan\n`,
    `- Parcel [JJD0001234567](bo:parcel/JJD0001234567${tenant}) was scanned twice but never loaded, `,
    `its [Data Gateway job](bo:job/JJD0001234567${tenant}) has no assignment\n`,
    ...(other
      ? [
          `- Depot [${getDepotName(other)}](bo:depot?tenantId=${other.tenant.id}&depotId=${other.id}) took over **3 tours** this morning\n`
        ]
      : []),
    `- All tours are on [Live Status](bo:live-status${depot}) and the [tour performance overview](bo:tour-performance${depot}), `,
    `and the [tenant health](bo:health${tenant}) shows no open issues`
  ];
};

export const UNSAFE_LINK_ANSWER_CHUNKS = [
  'These never become links: [external](https://example.com), ',
  '[script](javascript:void), [bad tour](bo:tour/not-a-uuid), ',
  '[unknown kind](bo:driver/42), [bad depot](bo:depot?depotId=abc), ',
  '[depot without access](bo:depot?depotId=999999999) and ',
  '[tenant without access](bo:parcel/1?tenantId=no-such-tenant).'
];

const FAILURES_CHART: ChatChart = {
  id: 'chart_1',
  type: 'bar',
  title: 'Unsuccessful deliveries per tour',
  categories: ['Tour 4835', 'Tour 4818'],
  series: [
    { name: 'Unknown reason', values: [39, 20] },
    { name: 'Absent', values: [1, 1] }
  ],
  unit: 'parcels',
  stacked: true
};

const UNSAFE_CHART: ChatChart = {
  ...FAILURES_CHART,
  id: 'chart_unsafe',
  categories: ['<img src=x onerror="window.bekkiXss=true">', 'Tour 4818'],
  series: [{ name: '<b>Absent</b>', values: [1, 1] }],
  stacked: false
};

export const CHART_ANSWER_CHUNKS = [
  'Tour **4835** had **40** unsuccessful deliveries today ',
  'and tour **4818** had **21**.\n\n',
  'Almost all of them, **59 of 61**, were recorded without a reason: ',
  'the driver app logged no structured outcome, which is worth following up.'
];

export const DELAYED_TOURS_TABLE: ChatTable = {
  id: 'table_1',
  title: 'Tours behind plan in depot 0052 & reasons',
  columns: [
    { label: 'Tour', type: 'text' },
    { label: 'Delay (> plan, min)', type: 'number' },
    { label: 'Delivered (%)', type: 'number' },
    { label: 'Reason', type: 'text' }
  ],
  rows: [
    ['4835', 52, 38.2, 'Late departure'],
    ['4818', 47, 45.5, 'Late departure (> 45 min)'],
    ['4807', 31, 71.3, null],
    ['4822', 18, 80.4, '<b>Traffic</b> &amp; roadworks']
  ]
};

const TABLE_ANSWER_CHUNKS = [
  '**3 tours** in depot **0052** are more than **30 minutes** behind plan. ',
  'Tours **4835** and **4818** left the depot late and have not caught up, ',
  'and tour **4807** lost time on the road.'
];

const getDeltaDelayMs = (): number => 60 + Math.round(Math.random() * 60);

// Runs mock payloads through the real parser and drops what it rejects, as the
// SSE transport does, so e2e exercises the same validation.
const parseOrDrop = <T>(
  parse: (payload: unknown) => T,
  payload: unknown
): T | null => {
  try {
    return parse(payload);
  } catch {
    return null;
  }
};

const hasTrigger = (message: string, keyword: string): boolean =>
  message.toLowerCase().includes(keyword);

const getPlainAnswerChunks = (
  { message, context }: ChatRequest,
  depots: MockDepot[]
): string[] | null => {
  if (hasTrigger(message, 'page')) {
    return [
      'This question was sent from:\n\n',
      `\`\`\`json\n${JSON.stringify(context.page ?? null, null, 2)}\n\`\`\``
    ];
  }
  if (hasTrigger(message, 'xss')) return XSS_ANSWER_CHUNKS;
  if (hasTrigger(message, 'link')) {
    return hasTrigger(message, 'unsafe')
      ? UNSAFE_LINK_ANSWER_CHUNKS
      : getLinkAnswerChunks(context, depots);
  }
  if (hasTrigger(message, 'weather')) return WEATHER_ANSWER_CHUNKS;
  return null;
};

export const useMockBekkiChatApi = (
  getDepots: () => MockDepot[] = () => []
): BekkiChatApi => {
  const contextFailedConversationIds = new Set<string>();

  let activeTurn: MockTurn | null = null;

  const stopTurn = (turn: MockTurn) => {
    turn.isAborted = true;
    if (turn.timeoutId !== null) {
      clearTimeout(turn.timeoutId);
      turn.timeoutId = null;
    }
    turn.cancelWait?.();
    turn.cancelWait = null;
  };

  const wait = (turn: MockTurn, ms: number): Promise<void> =>
    new Promise<void>((resolve) => {
      if (turn.isAborted) {
        resolve();
        return;
      }

      turn.cancelWait = resolve;
      turn.timeoutId = setTimeout(() => {
        turn.timeoutId = null;
        turn.cancelWait = null;
        resolve();
      }, ms);
    });

  const sendMessage = async (
    request: ChatRequest,
    onEvent: (event: ChatEvent) => void
  ): Promise<void> => {
    if (activeTurn) stopTurn(activeTurn);

    const turn: MockTurn = {
      isAborted: false,
      timeoutId: null,
      cancelWait: null
    };
    activeTurn = turn;

    const { conversationId, message } = request;

    const emit = (event: ChatEvent) => {
      if (!turn.isAborted) onEvent(event);
    };

    const emitError = (code: BekkiServerErrorCode) => {
      emit({ type: 'error', code });
    };

    const streamChunks = async (chunks: string[]) => {
      for (const text of chunks) {
        await wait(turn, getDeltaDelayMs());
        if (turn.isAborted) return;
        emit({ type: 'delta', text });
      }
    };

    emit({ type: 'start', messageId: crypto.randomUUID() });

    if (hasTrigger(message, 'busy')) {
      emitError('rate_limited');
      return;
    }

    if (
      hasTrigger(message, 'long') &&
      !contextFailedConversationIds.has(conversationId)
    ) {
      contextFailedConversationIds.add(conversationId);
      emitError('context_too_long');
      return;
    }

    emit({
      type: 'step',
      step: { id: 'reading', state: 'active' }
    });
    await wait(turn, STEP_READING_MS);
    if (turn.isAborted) return;

    if (hasTrigger(message, 'fail')) {
      emitError('backend_unavailable');
      return;
    }

    emit({ type: 'step', step: { id: 'reading', state: 'done' } });
    emit({ type: 'step', step: { id: 'analyzing', state: 'active' } });
    await wait(turn, STEP_ANALYZING_MS);
    if (turn.isAborted) return;

    emit({ type: 'step', step: { id: 'analyzing', state: 'done' } });

    const answerWith = async (shown: ChatEvent | null, chunks: string[]) => {
      if (shown) emit(shown);
      emit({ type: 'step', step: { id: 'synthesizing', state: 'active' } });
      await streamChunks(chunks);
      if (turn.isAborted) return;

      emit({ type: 'step', step: { id: 'synthesizing', state: 'done' } });
      emit({
        type: 'provenance',
        provenance: { dataAsOf: new Date().toISOString() }
      });
      emit({ type: 'done' });
    };

    if (hasTrigger(message, 'chart')) {
      const chart = parseOrDrop(
        parseChatChart,
        hasTrigger(message, 'unsafe') ? UNSAFE_CHART : FAILURES_CHART
      );
      await answerWith(
        chart ? { type: 'chart', chart } : null,
        CHART_ANSWER_CHUNKS
      );
      return;
    }

    if (hasTrigger(message, 'table')) {
      const table = parseOrDrop(parseChatTable, DELAYED_TOURS_TABLE);
      await answerWith(
        table ? { type: 'table', table } : null,
        TABLE_ANSWER_CHUNKS
      );
      return;
    }

    emit({ type: 'step', step: { id: 'synthesizing', state: 'active' } });

    if (hasTrigger(message, 'stop')) {
      await streamChunks(DEFAULT_ANSWER_CHUNKS.slice(0, 2));
      if (turn.isAborted) return;
      emitError('generation_stopped');
      return;
    }

    const plainAnswerChunks = getPlainAnswerChunks(request, getDepots());
    if (plainAnswerChunks) {
      await streamChunks(plainAnswerChunks);
      if (turn.isAborted) return;

      emit({ type: 'step', step: { id: 'synthesizing', state: 'done' } });
      emit({ type: 'done' });
      return;
    }

    await streamChunks(DEFAULT_ANSWER_CHUNKS);
    if (turn.isAborted) return;

    emit({ type: 'step', step: { id: 'synthesizing', state: 'done' } });
    emit({
      type: 'provenance',
      provenance: {
        dataAsOf: new Date().toISOString(),
        summary: '17 tours in depot 0052 today'
      }
    });
    emit({ type: 'done' });
  };

  const abort = () => {
    if (activeTurn) stopTurn(activeTurn);
    activeTurn = null;
  };

  return { sendMessage, abort };
};
